import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  Phone,
  Mail,
  User,
  Plus,
  ListChecks,
  BarChart2,
  Contact as ContactIcon,
  Paperclip,
  X,
  Trash2,
  FileText,
  Download,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  Play,
  Pause,
  Music,
  Film,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { humanSize } from "@/lib/fileUpload";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// ---------- Message renderers ----------

export function PollView({ poll, msgId, currentUserId, onVote }) {
  const votes = poll.votes || {};
  const total = Object.values(votes).reduce((a, u) => a + (u?.length || 0), 0);
  return (
    <div className="min-w-[220px] space-y-2" data-testid={`poll-${msgId}`}>
      <div className="flex items-center gap-1.5 font-semibold">
        <BarChart2 className="h-4 w-4 text-bloq-mint" /> {poll.question}
      </div>
      <div className="space-y-1.5">
        {poll.options.map((opt, i) => {
          const list = votes[String(i)] || [];
          const pct = total ? Math.round((list.length / total) * 100) : 0;
          const mine = list.includes(currentUserId);
          return (
            <button
              key={i}
              onClick={() => onVote(msgId, i)}
              className={cn(
                "relative block w-full overflow-hidden rounded-xl border text-left transition-colors",
                mine ? "border-bloq-mint/60" : "border-white/12 hover:border-white/25",
              )}
              data-testid={`poll-option-${msgId}-${i}`}
            >
              <span className="absolute inset-y-0 left-0 bg-bloq-mint/15" style={{ width: `${pct}%` }} />
              <span className="relative flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="flex items-center gap-1.5">
                  {mine && <Check className="h-3.5 w-3.5 text-bloq-mint" />}
                  {opt}
                </span>
                <span className="text-xs text-bloq-muted">{pct}%</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-bloq-muted">
        {total} vote{total === 1 ? "" : "s"}{poll.multi ? " · multiple choice" : ""}
      </div>
    </div>
  );
}

export function ChecklistView({ checklist, msgId, onToggle }) {
  const items = checklist.items || [];
  const done = items.filter((i) => i.done).length;
  return (
    <div className="min-w-[220px] space-y-1.5" data-testid={`checklist-${msgId}`}>
      <div className="flex items-center gap-1.5 font-semibold">
        <ListChecks className="h-4 w-4 text-bloq-mint" /> {checklist.title}
        <span className="ml-auto text-[11px] font-normal text-bloq-muted">{done}/{items.length}</span>
      </div>
      {items.map((it, i) => (
        <button
          key={i}
          onClick={() => onToggle(msgId, i)}
          className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left text-sm transition-colors hover:bg-white/5"
          data-testid={`checklist-item-${msgId}-${i}`}
        >
          <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border", it.done ? "border-bloq-mint bg-bloq-mint text-bloq-bg" : "border-white/30")}>
            {it.done && <Check className="h-3 w-3" />}
          </span>
          <span className={cn(it.done && "text-bloq-muted line-through")}>{it.text}</span>
        </button>
      ))}
    </div>
  );
}

export function ContactView({ contact, msgId }) {
  return (
    <div className="min-w-[200px] rounded-xl border border-white/10 bg-black/15 p-3" data-testid={`contact-${msgId}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-bloq-mint/15 text-bloq-mint">
          <User className="h-4 w-4" />
        </span>
        <span className="font-semibold">{contact.name}</span>
      </div>
      <div className="mt-2 space-y-1">
        {(contact.phones || []).map((p, i) => (
          <a key={`p${i}`} href={`tel:${p}`} className="flex items-center gap-1.5 text-xs text-bloq-muted hover:text-white">
            <Phone className="h-3 w-3" /> {p}
          </a>
        ))}
        {(contact.emails || []).map((e, i) => (
          <a key={`e${i}`} href={`mailto:${e}`} className="flex items-center gap-1.5 text-xs text-bloq-muted hover:text-white">
            <Mail className="h-3 w-3" /> {e}
          </a>
        ))}
      </div>
    </div>
  );
}

// BLOQ Shield file scan lifecycle: pending → clean, or a blocked/quarantined state.
const BLOCKED_SCAN = new Set(["infected", "malware", "blocked", "flagged", "unsafe", "rejected", "quarantined"]);

// Normalize the backend's media scan_status into one of the 5 authoritative states.
// Unknown/missing → "" (no badge, treated as a non-threat & viewable).
const SCAN_STATES = ["pending", "clean", "skipped", "unscannable", "infected"];
function normScan(raw) {
  const s = String(raw ?? "").toLowerCase().trim();
  return SCAN_STATES.includes(s) ? s : "";
}

function ScanBadge({ status, msgId }) {
  if (status === "clean") {
    return (
      <span className="mt-1 flex items-center gap-1 text-[11px] text-bloq-mint" data-testid={`file-scan-${msgId}`} title="Passed BLOQ Shield security scan">
        <ShieldCheck className="h-3 w-3" /> Scanned · clean
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="mt-1 flex items-center gap-1 text-[11px] text-amber-400" data-testid={`file-scan-${msgId}`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Scanning for threats…
      </span>
    );
  }
  if (status === "unscannable") {
    return (
      <span className="mt-1 flex items-center gap-1 text-[11px] text-bloq-muted" data-testid={`file-scan-${msgId}`} title="This file couldn’t be scanned by BLOQ Shield">
        <ShieldAlert className="h-3 w-3" /> Couldn’t be scanned
      </span>
    );
  }
  if (status === "infected") {
    return (
      <span className="mt-1 flex items-center gap-1 text-[11px] text-destructive" data-testid={`file-scan-${msgId}`}>
        <ShieldAlert className="h-3 w-3" /> Failed security scan
      </span>
    );
  }
  // "skipped", "" or unknown → no badge (non-threat, nothing to advertise).
  return null;
}

// Inline audio player card (Voice Memos, songs & recordings) — v1.2.2 parity.
function AudioPlayer({ src, name, size, msgId, canDownload }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };
  return (
    <div className="flex min-w-[240px] items-center gap-3 rounded-xl border border-white/10 bg-black/15 p-2.5" data-testid={`msg-audio-${msgId}`}>
      <button
        type="button"
        onClick={toggle}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bloq-mint/15 text-bloq-mint transition-colors hover:bg-bloq-mint/25"
        data-testid={`audio-play-${msgId}`}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{name || "Audio"}</div>
        <div className="flex items-center gap-1 text-[11px] text-bloq-muted" data-testid={`audio-meta-${msgId}`}>
          <Music className="h-3 w-3" />
          {playing || cur > 0 ? `${fmt(cur)} / ${fmt(dur)}` : `Audio · ${humanSize(size || 0)}`}
        </div>
      </div>
      {canDownload && (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-full p-1.5 text-bloq-muted transition-colors hover:text-white"
          data-testid={`audio-download-${msgId}`}
          title="Download"
        >
          <Download className="h-4 w-4" />
        </a>
      )}
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        className="hidden"
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onTimeUpdate={(e) => setCur(e.target.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCur(0); }}
      />
    </div>
  );
}

// Resolve a short-lived signed download URL for a file (SEC-003). Cached < 30-min TTL so it re-mints
// before expiry; falls back to the legacy ?access_token= URL if minting fails so media never breaks.
function useDownloadUrl(fileId, enabled) {
  const { data } = useQuery({
    queryKey: ["dl-url", fileId],
    enabled: !!fileId && enabled,
    staleTime: 25 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      try {
        const r = await api.mintDownloadUrl(fileId);
        if (r?.token) return `${API_BASE}/files/${fileId}/download?token=${encodeURIComponent(r.token)}`;
        if (r?.url) return r.url.startsWith("http") ? r.url : `${API_BASE.replace(/\/api$/, "")}${r.url}`;
      } catch {
        /* fall through to the deprecated URL */
      }
      return api.fileDownloadUrl(fileId);
    },
  });
  return data;
}

export function FileView({ file, msgId, onImageClick, isOwn }) {
  const fileId = file.id || file.file_id;
  const status = normScan(file.scan_status);
  const blocked = status === "infected"; // ONLY infected is a hard block.
  const mime = file.mime || "";
  const isImage = mime.startsWith("image/") || file.kind === "image";
  const isAudio = mime.startsWith("audio/") || file.kind === "audio";
  const isVideo = mime.startsWith("video/") || file.kind === "video";
  // Scan gate for playable media: the sender can always open their own upload; everyone else must
  // wait while a scan is pending (backend returns 423). clean/skipped/unscannable = playable.
  const pendingScan = status === "pending";
  const canPlay = isOwn || !pendingScan;
  // Download/save affordance (v1.2.3): non-senders lose it when allow_download is false OR the file
  // is unscannable (terminal lock). Streaming playback is unaffected. Senders always keep download.
  const canDownload = isOwn || (file.allow_download !== false && status !== "unscannable");
  // Only mint a URL once the file will actually be rendered (not blocked, not scan-held for a non-owner).
  const needsUrl = !blocked && !((isAudio || isVideo) && !canPlay);
  const downloadUrl = useDownloadUrl(fileId, needsUrl);

  // Blocked attachments are never rendered/openable — trust-gate safety.
  if (blocked) {
    return (
      <div className="min-w-[220px] rounded-xl border border-destructive/40 bg-destructive/10 p-3" data-testid={`file-blocked-${msgId}`}>
        <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
          <ShieldAlert className="h-4 w-4" /> Attachment blocked
        </div>
        <p className="mt-1 text-xs text-bloq-muted">
          “{file.name || "This file"}” was flagged as a threat by BLOQ Shield and can’t be opened.
        </p>
      </div>
    );
  }

  // Playable media awaiting a scan (non-sender) — hold playback until it clears.
  if ((isAudio || isVideo) && !canPlay) {
    return (
      <div className="min-w-[220px] rounded-xl border border-white/10 bg-black/15 p-3" data-testid={`file-scanning-${msgId}`}>
        <div className="flex items-center gap-2 text-sm text-amber-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning {isAudio ? "audio" : "video"} for threats…
        </div>
        <p className="mt-1 truncate text-xs text-bloq-muted">{file.name || "Attachment"}</p>
      </div>
    );
  }

  // Minting the signed download URL — brief loading state (like AuthImage / voice notes).
  if (!downloadUrl) {
    return (
      <div className="min-w-[220px] rounded-xl border border-white/10 bg-black/15 p-3" data-testid={`file-loading-${msgId}`}>
        <div className="flex items-center gap-2 text-sm text-bloq-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading attachment…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {isImage ? (
        <button
          type="button"
          onClick={() => onImageClick?.(downloadUrl)}
          className="block max-w-full overflow-hidden rounded-xl"
          data-testid={`file-image-${msgId}`}
        >
          <img src={downloadUrl} alt={file.name || "Shared image"} loading="lazy" className="max-h-72 w-auto max-w-full rounded-xl object-cover" />
        </button>
      ) : isAudio ? (
        <AudioPlayer src={downloadUrl} name={file.name} size={file.size} msgId={msgId} canDownload={canDownload} />
      ) : isVideo ? (
        <video
          src={downloadUrl}
          controls
          controlsList={!canDownload ? "nodownload noremoteplayback" : undefined}
          onContextMenu={!canDownload ? (e) => e.preventDefault() : undefined}
          preload="metadata"
          className="max-h-72 w-auto max-w-full rounded-xl"
          data-testid={`msg-video-${msgId}`}
        />
      ) : canDownload ? (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-[220px] items-center gap-3 rounded-xl border border-white/10 bg-black/15 p-3 transition-colors hover:border-white/25"
          data-testid={`file-doc-${msgId}`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bloq-mint/15 text-bloq-mint">
            <FileText className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{file.name || "Attachment"}</span>
            <span className="block text-[11px] text-bloq-muted">{humanSize(file.size || 0)}</span>
          </span>
          <Download className="h-4 w-4 shrink-0 text-bloq-muted" />
        </a>
      ) : (
        <div
          className="flex min-w-[220px] items-center gap-3 rounded-xl border border-white/10 bg-black/15 p-3"
          data-testid={`file-doc-locked-${msgId}`}
          title="Download locked"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/8 text-bloq-muted">
            <FileText className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{file.name || "Attachment"}</span>
            <span className="block text-[11px] text-bloq-muted">{humanSize(file.size || 0)}</span>
          </span>
          <Lock className="h-4 w-4 shrink-0 text-bloq-muted" />
        </div>
      )}
      <ScanBadge status={status} msgId={msgId} downloadLocked={!canDownload} />
    </div>
  );
}

// ---------- Compose extras (＋ menu + dialogs) ----------

function RowEditor({ label, rows, setRows, min, max, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-bloq-muted">{label}</Label>
      {rows.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={v}
            onChange={(e) => setRows(rows.map((r, j) => (j === i ? e.target.value : r)))}
            placeholder={`${placeholder} ${i + 1}`}
            className="h-10 rounded-xl border-white/12 bg-bloq-bg"
          />
          {rows.length > min && (
            <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-bloq-muted hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {rows.length < max && (
        <button type="button" onClick={() => setRows([...rows, ""])} className="flex items-center gap-1 text-xs font-semibold text-bloq-mint hover:opacity-80">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      )}
    </div>
  );
}

export function ComposeExtras({ onPoll, onChecklist, onContact, onAttachFile, onAttachAudio, onAttachVideo, busy, mobileItems = [] }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // 'poll' | 'checklist' | 'contact'

  // poll
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  // checklist
  const [title, setTitle] = useState("");
  const [items, setItems] = useState(["", ""]);
  // contact
  const [cName, setCName] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cEmail, setCEmail] = useState("");

  const reset = () => {
    setQuestion(""); setOptions(["", ""]); setMulti(false);
    setTitle(""); setItems(["", ""]);
    setCName(""); setCPhone(""); setCEmail("");
  };
  const close = () => { setDialog(null); reset(); };

  const submitPoll = () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (question.trim().length < 2 || opts.length < 2) return;
    onPoll({ question: question.trim(), options: opts, multi });
    close();
  };
  const submitChecklist = () => {
    const its = items.map((o) => o.trim()).filter(Boolean);
    if (title.trim().length < 2 || its.length < 1) return;
    onChecklist({ title: title.trim(), items: its });
    close();
  };
  const submitContact = () => {
    if (cName.trim().length < 2) return;
    onContact({
      name: cName.trim(),
      phones: cPhone.trim() ? [cPhone.trim()] : [],
      emails: cEmail.trim() ? [cEmail.trim()] : [],
    });
    close();
  };

  const MENU = [
    { key: "poll", label: "Poll", icon: BarChart2, onClick: () => setDialog("poll") },
    { key: "checklist", label: "Checklist", icon: ListChecks, onClick: () => setDialog("checklist") },
    { key: "contact", label: "Contact", icon: ContactIcon, onClick: () => setDialog("contact") },
    ...(onAttachVideo ? [{ key: "video", label: "Video", sub: "Clips up to 20MB", icon: Film, onClick: () => onAttachVideo() }] : []),
    ...(onAttachAudio ? [{ key: "audio", label: "Audio", sub: "Voice memos, songs & recordings · up to 20MB", icon: Music, onClick: () => onAttachAudio() }] : []),
    ...(onAttachFile ? [{ key: "file", label: "File", icon: Paperclip, onClick: () => onAttachFile() }] : []),
    // Composer buttons that don't fit a phone-width row live here on small screens only.
    ...mobileItems.map((m) => ({ ...m, mobileOnly: true })),
  ];

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint disabled:opacity-40"
          data-testid="compose-extras-btn"
          title="Add poll, checklist, contact, video, audio or file"
        >
          <Plus className="h-5 w-5" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute bottom-12 left-0 z-20 w-60 overflow-hidden rounded-2xl border border-white/12 bg-bloq-s1 p-1 shadow-xl" data-testid="compose-extras-menu">
              {MENU.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  // Fire synchronously in the tap tick — iOS Safari blocks a deferred input.click().
                  onClick={() => { setMenuOpen(false); m.onClick(); }}
                  className={cn("flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/5", m.mobileOnly && "sm:hidden")}
                  data-testid={`compose-extra-${m.key}`}
                >
                  <m.icon className="h-4 w-4 shrink-0 text-bloq-mint" />
                  <span className="min-w-0">
                    <span className="block">{m.label}</span>
                    {m.sub && <span className="block truncate text-[10px] text-bloq-muted">{m.sub}</span>}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Poll dialog */}
      <Dialog open={dialog === "poll"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="poll-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><BarChart2 className="h-4 w-4 text-bloq-mint" /> New poll</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Question</Label>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask something…" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="poll-question-input" />
            </div>
            <RowEditor label="Options" rows={options} setRows={setOptions} min={2} max={6} placeholder="Option" />
            <button type="button" onClick={() => setMulti((v) => !v)} className="flex items-center gap-2 text-sm text-bloq-muted" data-testid="poll-multi-toggle">
              <span className={cn("flex h-4 w-4 items-center justify-center rounded border", multi ? "border-bloq-mint bg-bloq-mint text-bloq-bg" : "border-white/30")}>{multi && <Check className="h-3 w-3" />}</span>
              Allow multiple choices
            </button>
          </div>
          <DialogFooter>
            <Button onClick={submitPoll} disabled={busy} className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="poll-submit">Create poll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checklist dialog */}
      <Dialog open={dialog === "checklist"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="checklist-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><ListChecks className="h-4 w-4 text-bloq-mint" /> New checklist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Launch tasks" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="checklist-title-input" />
            </div>
            <RowEditor label="Items" rows={items} setRows={setItems} min={1} max={12} placeholder="Item" />
          </div>
          <DialogFooter>
            <Button onClick={submitChecklist} disabled={busy} className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="checklist-submit">Create checklist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact dialog */}
      <Dialog open={dialog === "contact"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="contact-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><ContactIcon className="h-4 w-4 text-bloq-mint" /> Share a contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Name</Label>
              <Input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Full name" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="contact-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Phone (optional)</Label>
              <Input value={cPhone} onChange={(e) => setCPhone(e.target.value)} placeholder="+1 555 010 0000" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="contact-phone-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Email (optional)</Label>
              <Input value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="name@example.com" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="contact-email-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={submitContact} disabled={busy} className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="contact-submit">Share contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
